import type {
  TaskId,
  ProjectId,
  TaskStatus,
  Priority,
  UserId,
  MilestoneId,
  TaskTypeId,
  LabelId,
  DayStr,
  Task,
} from '../../types';
import { taskRef } from '../../helpers';
import type { RichDoc } from '../../rich-doc';
import { richDocFrom, richDocToText } from '../../rich-doc';

import { fanoutLast } from '../fanout';
import { addToIndex, removeFromIndex } from '../indexes';
import {
  isTerminal,
  maxSortKey,
  refreshMentions,
  snapshot,
  sortKeyBetween,
  taskKeys,
} from '../internal';
import { logActivity } from '../journal';
import { bump, requireCache } from '../runtime';

export function createTask(args: {
  id: TaskId;
  projectId: ProjectId;
  title: string;
  parentId?: TaskId | null;
  status?: TaskStatus;
  priority?: Priority;
  assigneeId?: UserId | null;
  milestoneId?: MilestoneId | null;
  typeId?: TaskTypeId | null;
  labelIds?: LabelId[];
  estimateMinutes?: number | null;
  startOn?: DayStr | null;
  dueOn?: DayStr | null;
}): Task {
  const cache = requireCache();
  const project = cache.projects.get(args.projectId);
  if (project === undefined) {
    throw new Error('Проект не найден: создание задачи невозможно');
  }
  const number = project.nextTaskNumber;
  project.nextTaskNumber += 1;
  const now = cache.nowMs();
  const status = args.status ?? 'backlog';
  const siblings: string[] = [];
  for (const id of cache.index.tasksByProject.get(args.projectId) ?? []) {
    const sibling = cache.tasks.get(id);
    if (
      sibling !== undefined &&
      sibling.status === status &&
      sibling.deletedAt === null
    ) {
      siblings.push(sibling.sortKey);
    }
  }
  const parentId = args.parentId ?? null;
  if (parentId !== null) {
    const parent = cache.tasks.get(parentId);
    if (parent === undefined) {
      throw new Error('Родительская задача не найдена');
    }
    if (parent.parentId !== null) {
      throw new Error('У подзадачи не может быть подзадач: третьего уровня нет');
    }
  }
  const task: Task = {
    id: args.id,
    key: taskRef(project.key, number),
    workspaceId: project.workspaceId,
    projectId: args.projectId,
    parentId,
    depth: parentId === null ? 0 : 1,
    typeId: args.typeId ?? null,
    milestoneId: args.milestoneId ?? null,
    title: args.title,
    descriptionJson: [],
    descriptionText: '',
    status,
    priority: args.priority ?? 'none',
    assigneeId: args.assigneeId ?? null,
    creatorId: cache.me,
    labelIds: args.labelIds !== undefined ? [...args.labelIds] : [],
    estimateMinutes: args.estimateMinutes ?? null,
    startOn: args.startOn ?? null,
    dueOn: args.dueOn ?? null,
    sortKey: sortKeyBetween(maxSortKey(siblings), null),
    createdAt: now,
    updatedAt: now,
    completedAt: null,
    deletedAt: null,
  };
  cache.tasks.set(task.id, task);
  addToIndex(cache, 'task', String(task.id));
  logActivity(cache, {
    mutator: 'task.create',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'insert',
    before: null,
    after: snapshot(task),
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id));
  return task;
}

export function setTaskTitle(args: { id: TaskId; title: string }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { title: task.title, updatedAt: task.updatedAt };
  task.title = args.title;
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setTitle',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { title: task.title, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setTaskDescription(args: { id: TaskId; bodyJson: RichDoc }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = {
    descriptionJson: task.descriptionJson,
    descriptionText: task.descriptionText,
    updatedAt: task.updatedAt,
  };
  task.descriptionJson = richDocFrom(args.bodyJson);
  task.descriptionText = richDocToText(task.descriptionJson);
  task.updatedAt = cache.nowMs();
  refreshMentions(cache, 'task', String(task.id), task.descriptionText);
  logActivity(cache, {
    mutator: 'task.setDescription',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { descriptionText: task.descriptionText, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setStatus(args: { id: TaskId; status: TaskStatus }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const wasTerminal = isTerminal(task.status);
  const willTerminal = isTerminal(args.status);
  const before = { status: task.status, completedAt: task.completedAt, updatedAt: task.updatedAt };
  removeFromIndex(cache, 'task', String(task.id));
  task.status = args.status;
  if (willTerminal && !wasTerminal) {
    task.completedAt = cache.nowMs();
  } else if (!willTerminal && wasTerminal) {
    task.completedAt = null;
  }
  task.updatedAt = cache.nowMs();
  addToIndex(cache, 'task', String(task.id));
  logActivity(cache, {
    mutator: 'task.setStatus',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { status: task.status, completedAt: task.completedAt, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function moveTask(args: {
  id: TaskId;
  status?: TaskStatus;
  projectId?: ProjectId;
  beforeId?: TaskId;
  afterId?: TaskId;
}): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const oldProjectId = task.projectId;
  const before = {
    status: task.status,
    projectId: task.projectId,
    sortKey: task.sortKey,
    updatedAt: task.updatedAt,
  };
  removeFromIndex(cache, 'task', String(task.id));
  if (args.status !== undefined) {
    task.status = args.status;
  }
  if (args.projectId !== undefined) {
    const project = cache.projects.get(args.projectId);
    if (project === undefined) {
      throw new Error('Проект не найден: перенос задачи невозможен');
    }
    task.projectId = args.projectId;
  }
  if (args.beforeId !== undefined || args.afterId !== undefined) {
    const beforeKey = args.beforeId !== undefined ? cache.tasks.get(args.beforeId)?.sortKey ?? null : null;
    const afterKey = args.afterId !== undefined ? cache.tasks.get(args.afterId)?.sortKey ?? null : null;
    task.sortKey = sortKeyBetween(beforeKey, afterKey);
  } else if (args.status !== undefined || args.projectId !== undefined) {
    const siblings: string[] = [];
    for (const id of cache.index.tasksByProject.get(task.projectId) ?? []) {
      const sibling = cache.tasks.get(id);
      if (
        sibling !== undefined &&
        sibling.id !== task.id &&
        sibling.status === task.status &&
        sibling.deletedAt === null
      ) {
        siblings.push(sibling.sortKey);
      }
    }
    task.sortKey = sortKeyBetween(maxSortKey(siblings), null);
  }
  task.updatedAt = cache.nowMs();
  addToIndex(cache, 'task', String(task.id));
  logActivity(cache, {
    mutator: 'task.move',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { status: task.status, projectId: task.projectId, sortKey: task.sortKey, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  const keys = taskKeys(cache, task.id);
  if (String(oldProjectId) !== String(task.projectId)) {
    keys.push(`board:${String(oldProjectId)}`);
  }
  keys.push('inbox');
  bump(...keys);
}

export function setAssignee(args: { id: TaskId; assigneeId: UserId | null }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { assigneeId: task.assigneeId, updatedAt: task.updatedAt };
  removeFromIndex(cache, 'task', String(task.id));
  task.assigneeId = args.assigneeId;
  task.updatedAt = cache.nowMs();
  addToIndex(cache, 'task', String(task.id));
  logActivity(cache, {
    mutator: 'task.setAssignee',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { assigneeId: task.assigneeId, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setPriority(args: { id: TaskId; priority: Priority }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { priority: task.priority, updatedAt: task.updatedAt };
  task.priority = args.priority;
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setPriority',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { priority: task.priority, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setEstimate(args: { id: TaskId; estimateMinutes: number | null }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { estimateMinutes: task.estimateMinutes, updatedAt: task.updatedAt };
  task.estimateMinutes = args.estimateMinutes;
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setEstimate',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { estimateMinutes: task.estimateMinutes, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setTaskDates(args: { id: TaskId; startOn?: DayStr | null; dueOn?: DayStr | null }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { startOn: task.startOn, dueOn: task.dueOn, updatedAt: task.updatedAt };
  if (args.startOn !== undefined) {
    task.startOn = args.startOn;
  }
  if (args.dueOn !== undefined) {
    task.dueOn = args.dueOn;
  }
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setDates',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { startOn: task.startOn, dueOn: task.dueOn, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setMilestone(args: { id: TaskId; milestoneId: MilestoneId | null }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { milestoneId: task.milestoneId, updatedAt: task.updatedAt };
  task.milestoneId = args.milestoneId;
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setMilestone',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { milestoneId: task.milestoneId, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setLabels(args: { id: TaskId; labelIds: LabelId[] }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { labelIds: [...task.labelIds], updatedAt: task.updatedAt };
  task.labelIds = [...args.labelIds];
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setLabels',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { labelIds: [...task.labelIds], updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function setTaskType(args: { id: TaskId; typeId: TaskTypeId | null }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { typeId: task.typeId, updatedAt: task.updatedAt };
  task.typeId = args.typeId;
  task.updatedAt = cache.nowMs();
  logActivity(cache, {
    mutator: 'task.setType',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { typeId: task.typeId, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id), 'inbox');
}

export function softDeleteTask(args: { id: TaskId }): void {
  const cache = requireCache();
  const task = cache.tasks.get(args.id);
  if (task === undefined) {
    throw new Error('Задача не найдена');
  }
  const before = { deletedAt: task.deletedAt, updatedAt: task.updatedAt };
  removeFromIndex(cache, 'task', String(task.id));
  task.deletedAt = cache.nowMs();
  task.updatedAt = task.deletedAt;
  logActivity(cache, {
    mutator: 'task.softDelete',
    entityTable: 'task',
    entityId: String(task.id),
    op: 'update',
    before,
    after: { deletedAt: task.deletedAt, updatedAt: task.updatedAt },
  });
  fanoutLast(cache);
  bump(...taskKeys(cache, task.id));
}
